/**
 * <wc-brief>: the five-step commission brief on a portrait's product page
 * (plan §3.3). The rules live in wc-brief-core.js; this file is the element.
 *
 * Why it is shaped like this, each learned from Horizon 4.2.0's source:
 *
 * - IT OWNS ITS FORM. Horizon's product form submits through a document-level
 *   capture listener, and an Add clicked while a variant change is in flight is
 *   queued with only `id` and `quantity` (product-form.js #createQueuedAddToCartItem),
 *   dropping every property and the selling plan. So the brief never uses it:
 *   its form has no `on:submit`, and Add is a plain button that runs `add()`.
 * - HORIZON STILL OWNS THE VARIANT. One Horizon <variant-picker> sits inside
 *   this block, hidden. The brief's cards tick its radios by
 *   `data-option-value-id` (never by label: Translate & Adapt renames values per
 *   market) and fire `change`, so price, media and `?variant=` stay Horizon's.
 *   Next and Add wait for that ProductSelectEvent's promise.
 * - THE STEP LIVES IN location.hash, because Horizon calls
 *   `history.replaceState({})` on every variant change and would wipe anything
 *   kept in history.state. The browser's Back walks the steps.
 * - ONE ADD, ALL LINES. `POST /cart/add.js` with `items[]` — the portrait, the
 *   extra subjects and the extras, each carrying `_brief_id` — then a
 *   CartLinesUpdateEvent WITH a promise: Horizon's drawer ignores the event
 *   without one (component-cart-items.js #handleCartUpdate).
 * - FOCUS MODE IS CSS. On a phone the brief goes full-screen by an attribute,
 *   never by moving it: a moved <iframe> reloads, and the upload app may be one.
 */

const PRODUCT_SELECT_FALLBACK = "shopify:product:select";

/**
 * Decode HTML entities in every string of an object, in place.
 * @param {Record<string, unknown> | undefined} strings
 */
function decodeStrings(strings) {
  if (!strings) return;
  const box = document.createElement("textarea");
  for (const [key, value] of Object.entries(strings)) {
    if (typeof value !== "string" || !value.includes("&")) continue;
    box.innerHTML = value;
    strings[key] = box.value;
  }
}

/**
 * The brief's own named controls, and Shopify's form plumbing. Anything else
 * named in the form, outside the upload slot, was written there by an app.
 */
const OURS = /^(wc-|id$|quantity$|form_type$|utf8$|add$|product-id$|section-id$)/;
/** App containers known to write into product forms (their DOM, never moved). */
const APP_BOXES =
  '#infiniteoptions-container, .infiniteoptions, [class*="customizery"], [id*="uploadkit"], [class*="uploadkit"]';

/** The brief's button, for Uploadly's buttons (their inline !important styles). */
const UPLOAD_BUTTON = [
  ["font-size", "1.0625rem"],
  ["font-weight", "600"],
  ["border-radius", "var(--b-radius)"],
  ["padding", "12px 20px"],
  ["min-height", "48px"],
  ["height", "auto"],
  ["line-height", "1.3"],
  ["white-space", "normal"],
];

/** @typedef {import('./wc-brief-core.js')} Core */

class WcBrief extends HTMLElement {
  /** @type {Core} */ core;
  /** @type {any} */ events;
  /** @type {(cents: number, format: string, currency: string) => string} */ formatMoney;
  /** @type {any} */ data;
  /** @type {HTMLFormElement} */ form;
  /** @type {string} */ step = "who";
  /** @type {Promise<any> | null} */ pending = null;
  /** @type {string[]} */ options = [];
  /** @type {string} */ briefId = "";
  /** @type {boolean} */ returnToReview = false;
  /** @type {number} */ pushed = 0;
  /** @type {Set<string>} */ tracked = new Set();
  /** @type {AbortController} */ abort = new AbortController();
  /** @type {number | undefined} */ saveTimer;
  /** @type {number | undefined} */ announceTimer;
  /** @type {boolean} */ adding = false;
  /** @type {boolean} */ restoring = false;
  /** @type {MediaQueryList} */ phone = matchMedia("(max-width: 749px)");

  connectedCallback() {
    // ChatBase's launcher floats bottom-right, exactly where the bar's Next sits
    // on a tablet (L-35, 8 Oct). While the brief is on screen it rises above the
    // bar, by the bar's own height (wc-brief.css, 750 px and up; a phone hides it
    // in focus mode, and W5's cart bar owns it below 750).
    this.inView = new IntersectionObserver(([entry]) => {
      const root = document.documentElement;
      root.toggleAttribute("data-wc-brief-bar", entry.isIntersecting);
      root.style.setProperty("--wc-brief-bar-h", `${this.querySelector(".wc-brief__bar")?.offsetHeight || 0}px`);
    });
    this.inView.observe(this);
    this.init().catch((error) => {
      // A brief that cannot start must say so and leave the page usable. The
      // noscript-style fallback in the block tells the shopper how to order.
      console.error("[wc-brief] could not start", error);
      this.setAttribute("data-failed", "");
    });
  }

  disconnectedCallback() {
    this.abort.abort();
    this.inView?.disconnect();
    document.documentElement.removeAttribute("data-wc-brief-bar");
    document.documentElement.classList.remove("wc-brief-open");
  }

  async init() {
    const script = this.querySelector("script[data-wc-brief-data]");
    this.data = JSON.parse(script?.textContent || "{}");
    // Shopify's `t` filter HTML-escapes what it returns ("Style &amp; artist"),
    // and these strings are set as text, so they are decoded once, here.
    decodeStrings(this.data.stepNames);
    decodeStrings(this.data.labels);
    decodeStrings(this.data.messages);
    // Each module by its own versioned asset URL, so a theme change is never
    // served from a stale CDN copy of a sibling file.
    const [core, events, money, saveModule, review, cart, photos] = await Promise.all([
      import(this.dataset.core),
      import("@shopify/events").catch(() => null),
      import("@theme/money-formatting").catch(() => null),
      // Autosave is a convenience: if it cannot load, the brief still works.
      import(this.dataset.save).catch(() => null),
      // The review and the add are needed, like the core; they are separate
      // files so this one stays under theme check's size limit.
      import(this.dataset.review),
      import(this.dataset.cart),
      // Who is in each photo (A33); without it, the one-name-per-subject list.
      import(this.dataset.photos).catch(() => null),
    ]);
    this.photos = photos;
    this.saveModule = saveModule;
    this.review = review;
    this.cart = cart;
    this.core = core;
    this.events = events;
    this.formatMoney = money?.formatMoney || ((cents) => (cents / 100).toFixed(2));
    this.data.moneyFormat = core.moneyFormatFromSample(this.data.moneySample);

    this.form = /** @type {HTMLFormElement} */ (this.querySelector("form[data-wc-form]"));
    if (!this.form) throw new Error("brief form missing");

    // Which Frame values are print-only or canvas: decided once, by core.
    for (const value of this.data.options[this.data.positions.frame]?.values || []) {
      value.finish = core.finishOf(value.name);
    }
    // A first or ?variant= choice this market does not sell moves to one it
    // does (W2's rule, core.settleOptions), and Horizon's picker follows.
    const picked = this.readPickerOptions();
    this.options = core.settleOptions(this.data, picked);
    this.syncOptionRadios();
    this.briefId = core.newBriefId();

    this.bind();
    if (this.options.join() !== picked.join()) {
      this.syncVariantId();
      this.tickPicker(this.options);
    }
    this.guardForm();
    this.renderSubjects();
    this.renderAll();
    // What the price covers (Tier 1): "Includes 3 people" on Family, 2 on
    // Couple and Wedding, "1 pet" on Pet, "1 person or pet" on Memorial.
    const covers = core.includedNote(this.data);
    const coversEl = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-included]"));
    if (coversEl && covers) {
      const labels = this.data.labels;
      const words =
        covers.key === "people"
          ? labels.includedPeople
          : covers.key === "pets"
            ? labels.includedPets
            : labels.includedEither;
      coversEl.textContent = words.replace("%n%", String(covers.count));
      coversEl.hidden = false;
    }
    this.offerResume();
    this.route({ initial: true });
    // A phone reloaded on a step's hash opens straight into focus mode: the
    // saved brief is the first thing there, not an empty step beneath it.
    if (this.pendingResume && this.hasAttribute("data-focus")) {
      this.scrollTop = 0;
      /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-resume-yes]"))?.focus({ preventScroll: true });
    }
    this.toggleAttribute("data-ready", true);
  }

  // ---------------------------------------------------------------- binding

  /**
   * Infinite Options' ScriptTag writes #infiniteoptions-container into any
   * product form, this one included (f3, 2026-09-25), and it stays on: the
   * control reads Text to print and Font through it. The add reads only the
   * upload slot, but any other reader of this form would take its fields. So
   * each foreign named control is DISABLED (never submitted, never in
   * FormData, never focused) and a known app box is INERT. Neither is moved.
   */
  guardForm() {
    const slot = this.querySelector("[data-wc-uploads]");
    const run = () => {
      // Uploadly builds each file's fields in a box, then appends the box to the
      // END of the first cart/add form (uploadly.js), outside the slot, where the
      // pass below would judge them foreign and the add would drop the photos
      // (live, dev, 2026-09-25). Its boxes are brought back into the slot first.
      // Uploadly finds them by id, anywhere in the document, so the move is safe.
      if (slot) {
        for (const box of document.querySelectorAll('[id^="imageFiles_"][data-img-id]')) {
          if (!slot.contains(box)) slot.append(box);
        }
      }
      for (const el of /** @type {any} */ (this.form.elements)) {
        const name = el.getAttribute("name");
        if (!name || OURS.test(name) || slot?.contains(el) || el.disabled) continue;
        el.disabled = true;
        el.setAttribute("data-wc-foreign", "");
      }
      // Inert AND hidden: dead UI inside the brief, and Infinite Options' font
      // field reads "Original", a word the claims register bans (K9, 27 Sep).
      for (const box of this.form.querySelectorAll(APP_BOXES)) {
        if (slot?.contains(box)) continue;
        /** @type {HTMLElement} */ (box).inert = true;
        /** @type {HTMLElement} */ (box).hidden = true;
      }
      // Uploadly's buttons carry inline !important styles (14 px, 3 px corners,
      // width auto) that no stylesheet can beat: the brief's are set inline.
      for (const b of /** @type {NodeListOf<HTMLElement>} */ (
        slot?.querySelectorAll(".uploadcare--widget__button, .uploadcare--widget__file-name") || []
      )) {
        const open = !b.matches(".uploadcare--widget__button_type_cancel, .uploadcare--widget__button_type_remove");
        // "Change image(s)" is a div: a button's role, a tab stop, and Enter or
        // Space. Taking keydown suppresses a keypress Uploadcare might act on,
        // and the click waits a tick so it never opens a dialog already open.
        if (b.tagName === "DIV" && !b.dataset.wcKeys) {
          b.dataset.wcKeys = "";
          b.setAttribute("role", "button");
          b.tabIndex = 0;
          b.addEventListener("keydown", (e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            setTimeout(() => document.querySelector(".uploadcare--dialog") || b.click());
          });
        }
        for (const [k, v] of [
          ...UPLOAD_BUTTON,
          ["width", open ? "100%" : "auto"],
          ["color", open ? "#fff" : "var(--b-navy)"],
        ]) {
          if (b.style.getPropertyValue(k) !== v) b.style.setProperty(k, v, "important");
        }
      }
      // Its label is blank unless one is set in Uploadly; a blank one only adds space.
      const label = /** @type {HTMLElement | null} */ (slot?.querySelector('[id^="selBtn_"] > p'));
      if (label) label.hidden = !label.textContent?.trim();
      // The upload app shows or hides every [name="add"] by inline style around
      // an upload, which put Add on the Photos step (Luke, live, 15 Oct). The
      // brief alone decides when Add shows: Review.
      for (const b of /** @type {NodeListOf<HTMLElement>} */ (this.form.querySelectorAll('[name="add"]'))) {
        for (const k of ["display", "visibility", "opacity"])
          if (b.style.getPropertyValue(k)) b.style.removeProperty(k);
      }
      // Waiting for the upload app to replace a forced "later" (A34).
      if (this.photosAutoLater) this.renderPhotos();
      // A photo added or removed redraws who is in each (A33).
      this.photos?.render(this);
    };
    run();
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        run();
      });
    });
    // style: Uploadly rewrites its buttons' inline styles when its state changes.
    observer.observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
    this.abort.signal.addEventListener("abort", () => observer.disconnect());
  }

  bind() {
    const { signal } = this.abort;
    this.form.addEventListener("change", (e) => this.onFieldChange(e), { signal });
    this.form.addEventListener("input", (e) => this.onFieldInput(e), { signal });
    // No native submit, ever: a plain button runs add(), and Enter is taken
    // before implicit submission can fire. Anything that still submits (an
    // injected app button) is stopped here.
    this.form.addEventListener("submit", (e) => e.preventDefault(), { signal, capture: true });
    this.form.addEventListener("keydown", (e) => this.onKeydown(e), { signal });
    this.addEventListener("click", (e) => this.onClick(e), { signal });
    this.addEventListener(
      "pointerdown",
      (e) => {
        const input = /** @type {HTMLInputElement | null} */ (
          /** @type {HTMLElement} */ (e.target).closest(".wc-brief-chip")?.querySelector('input[type="radio"]') || null
        );
        this.chipDown = input;
        this.chipWasChecked = Boolean(input?.checked);
      },
      { signal },
    );
    window.addEventListener("hashchange", () => this.route(), { signal });
    window.addEventListener("popstate", () => this.route(), { signal });
    this.phone.addEventListener("change", () => this.applyFocusMode(), { signal });

    const section = this.closest(".shopify-section") || document;
    const productSelect = this.events?.StandardEvents?.productSelect || PRODUCT_SELECT_FALLBACK;
    section.addEventListener(productSelect, (e) => this.onProductSelect(e), { signal });
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    // An Edit link from the review comes BACK to the review: Next says so.
    if (target.closest("[data-wc-edit]")) {
      this.returnToReview = true;
      this.pushed++;
      return;
    }
    this.onChipClick(target);
    const control = target.closest(
      "[data-wc-next],[data-wc-back],[data-wc-add],[data-wc-start],[data-wc-close],[data-wc-count],[data-wc-resume-yes],[data-wc-resume-no],[data-wc-email-send]",
    );
    if (!control || !this.contains(control)) return;
    if (control instanceof HTMLAnchorElement) event.preventDefault();
    this.engage();

    if (control.hasAttribute("data-wc-next")) this.next();
    else if (control.hasAttribute("data-wc-back")) this.back();
    else if (control.hasAttribute("data-wc-add")) this.add();
    else if (control.hasAttribute("data-wc-start")) this.start();
    else if (control.hasAttribute("data-wc-close")) this.close();
    else if (control.hasAttribute("data-wc-count")) this.bump(/** @type {HTMLElement} */ (control));
    else if (control.hasAttribute("data-wc-resume-yes")) this.resume(true);
    else if (control.hasAttribute("data-wc-resume-no")) this.resume(false);
    else if (control.hasAttribute("data-wc-email-send")) this.emailBrief();
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === "Escape" && this.hasAttribute("data-focus")) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === "Tab" && this.hasAttribute("data-focus")) this.trapTab(event);
    if (event.key !== "Enter" || event.isComposing) return;
    const field = /** @type {HTMLElement} */ (event.target);
    // Enter in a text box is a line break, and on a button it is a click.
    // Everywhere else it would submit the form: it never does. Before the
    // review step it means Next; on the review step it does nothing, so Add
    // is always a deliberate press.
    if (field instanceof HTMLTextAreaElement || field instanceof HTMLButtonElement) return;
    if (field instanceof HTMLAnchorElement) return;
    event.preventDefault();
    if (this.step !== "review") this.next();
  }

  /** @param {Event} event */
  onFieldChange(event) {
    const field = /** @type {HTMLInputElement} */ (event.target);
    const name = field.name || "";
    if (name === "wc-style") this.selectOption(this.data.positions.style, field.value);
    else if (name === "wc-size") this.selectOption(this.data.positions.size, field.value);
    else if (name === "wc-frame") this.selectOption(this.data.positions.frame, field.value);
    else if (name === "wc-finish") this.selectFinish(field.value);
    else if (name === "wc-memorial") this.setMemorial(field.checked);
    else if (name === "wc-people" || name === "wc-pets") this.renderSubjects();
    else if (name === "wc-artist") this.clearArtistReset();
    else if (name === "wc-photos") this.photosAutoLater = false;
    this.engage();
    this.clearErrors(field);
    this.renderAll();
    this.scheduleSave();
  }

  /** @param {Event} event */
  onFieldInput(event) {
    const field = /** @type {HTMLInputElement} */ (event.target);
    if (field.name === "wc-people" || field.name === "wc-pets") this.renderSubjects();
    this.renderAll();
    this.scheduleSave();
  }

  // ---------------------------------------------------------------- state

  /** Everything the rules need, read from the form: the form is the state. */
  readState() {
    const f = this.form;
    const value = (name) =>
      /** @type {HTMLInputElement | null} */ (f.querySelector(`[name="${name}"]:checked`))?.value ?? "";
    const text = (name) => /** @type {HTMLInputElement | null} */ (f.elements.namedItem(name))?.value ?? "";
    const checked = (name) =>
      Boolean(/** @type {HTMLInputElement | null} */ (f.querySelector(`[name="${name}"]`))?.checked);
    const extras = {};
    for (const box of f.querySelectorAll("input[data-wc-extra]")) {
      extras[/** @type {HTMLInputElement} */ (box).dataset.wcExtra] = /** @type {HTMLInputElement} */ (box).checked;
    }
    const upload = this.uploadState();
    return {
      briefId: this.briefId,
      options: [...this.options],
      people: text("wc-people"),
      pets: text("wc-pets"),
      recipient: value("wc-recipient"),
      occasion: value("wc-occasion"),
      memorial: this.data.memorialProduct || checked("wc-memorial"),
      artist: value("wc-artist") || this.core.NEXT_AVAILABLE,
      subjects: [...f.querySelectorAll('input[name="wc-subject"]')].map(
        (i) => /** @type {HTMLInputElement} */ (i).value,
      ),
      photos: /** @type {'now' | 'later' | ''} */ (value("wc-photos")),
      notes: text("wc-notes"),
      addText: checked("wc-add-text"),
      textLine: text("wc-text-line"),
      textName: text("wc-text-name"),
      textDates: text("wc-text-dates"),
      font: value("wc-font"),
      angelWings: checked("wc-angel-wings"),
      extras,
      payment: value("wc-plan"),
      photoCount: upload.detectable ? upload.count : undefined,
      photoPeople: this.photos?.read(this) || [],
    };
  }

  /** What the upload app holds: assets/wc-brief-cart.js. */
  uploadState() {
    return this.cart.uploadState(this);
  }

  // ---------------------------------------------------------------- variants

  /** @returns {HTMLElement | null} */
  get picker() {
    return this.querySelector("[data-wc-picker] variant-picker");
  }

  /** The option value ids Horizon's picker has checked, in option order. */
  readPickerOptions() {
    const picker = this.picker;
    const ids = picker
      ? [...picker.querySelectorAll("fieldset input:checked, select option:checked")].map(
          (el) => /** @type {HTMLElement} */ (el).dataset.optionValueId || "",
        )
      : [];
    if (ids.length === this.data.optionCount && ids.every(Boolean)) return ids;
    return (this.data.initialOptions || []).map(String);
  }

  /**
   * Move one option, keeping the others where that combination exists.
   * @param {number} position
   * @param {string} valueId
   */
  selectOption(position, valueId) {
    const next = this.core.resolveCombination(this.data, this.options, position, valueId);
    if (!next) {
      this.showErrors(this.step, [{ field: "size", code: "combination_missing" }]);
      return;
    }
    const moved = next.filter((id, i) => i !== position && id !== this.options[i]);
    this.options = next;
    this.syncOptionRadios();
    const note = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-adjusted]"));
    if (note) {
      // Said on screen, not only to a screen reader: the finish changed itself.
      note.textContent = moved.length ? this.message("adjusted") : "";
      note.hidden = !moved.length;
    }
    if (moved.length) this.announce(this.message("adjusted"));
    this.tickPicker(next);
  }

  /** @param {string} finish - framed | print | canvas */
  selectFinish(finish) {
    const pos = this.data.positions.frame;
    const current = this.options[pos];
    const fixed = this.options.map((id, i) => (i === pos ? null : id));
    const available = this.core.availableValues(this.data, pos, fixed);
    const values = this.data.options[pos].values.filter((v) => v.finish === finish && available.has(String(v.id)));
    if (!values.length) return;
    const keep = values.find((v) => String(v.id) === String(current));
    this.selectOption(pos, String((keep || values[0]).id));
  }

  /**
   * Tick Horizon's radios for a whole combination, then fire ONE change, so
   * Horizon makes one request for the final selection (a change per option
   * would abort and refetch).
   * @param {string[]} ids
   */
  tickPicker(ids) {
    const picker = this.picker;
    if (!picker) return;
    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let last = null;
    for (const id of ids) {
      const radio = /** @type {HTMLInputElement | null} */ (
        picker.querySelector(`input[data-option-value-id="${CSS.escape(id)}"]`)
      );
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          last = radio;
        }
        continue;
      }
      // Dropdown mode is supported for safety; the block asks for buttons.
      const option = /** @type {HTMLOptionElement | null} */ (
        picker.querySelector(`option[data-option-value-id="${CSS.escape(id)}"]`)
      );
      if (option && !option.selected) {
        option.selected = true;
        last = option.closest("select");
      }
    }
    if (!last) return;
    // Busy from the tick, not from Horizon's event: nothing may slip between.
    // If Horizon never answers (its picker module not upgraded yet), the brief
    // must not stay locked: our own matrix already knows the variant.
    this.setBusy(true);
    const before = this.pending;
    last.dispatchEvent(new Event("change", { bubbles: true }));
    window.setTimeout(() => {
      if (this.pending === before && this.hasAttribute("data-busy")) {
        const variant = this.core.variantFor(this.data, this.options);
        const idInput = /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[name="id"]'));
        if (variant && idInput) idInput.value = String(variant.id);
        this.setBusy(false);
      }
    }, 2500);
  }

  /**
   * The form's hidden `name="id"` on the brief's variant NOW, not when
   * Horizon's select event answers: K1's W-G reads it straight after a
   * `?variant=` this market does not sell (W2's nearest offered one).
   */
  syncVariantId() {
    const variant = this.core.variantFor(this.data, this.options);
    const idInput = /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[name="id"]'));
    if (variant && idInput) idInput.value = String(variant.id);
  }

  /** @param {any} event - ProductSelectEvent */
  onProductSelect(event) {
    const picker = this.picker;
    if (!picker || !(event.target instanceof Node) || !picker.contains(event.target)) return;
    const promise = event.promise || Promise.resolve(null);
    this.pending = promise;
    this.setBusy(true);
    promise
      .then((result) => {
        if (this.pending !== promise) return; // a newer selection took over
        const resource = result?.detail?.resource;
        const idInput = /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[name="id"]'));
        if (resource?.id && idInput) idInput.value = String(resource.id);
        if (!resource || resource.available === false) {
          this.showErrors("size", [{ field: "size", code: "variant_unavailable" }]);
        }
      })
      .catch((error) => {
        // An aborted fetch means a newer change replaced it; anything else
        // leaves our own matrix in charge, which already knows the variant.
        if (error?.name !== "AbortError") console.warn("[wc-brief] variant change failed", error);
      })
      .finally(() => {
        if (this.pending !== promise) return;
        this.pending = null;
        this.options = this.readPickerOptions();
        this.syncOptionRadios();
        this.setBusy(false);
        this.renderAll();
      });
  }

  /** @param {boolean} busy */
  setBusy(busy) {
    this.toggleAttribute("data-busy", busy);
    for (const button of this.querySelectorAll("[data-wc-next],[data-wc-add]")) {
      const b = /** @type {HTMLButtonElement} */ (button);
      // Add waits while a variant resolves or an add is in flight. Next never
      // waits for the variant: the brief's own matrix already knows it, and a
      // 0.4-1.25 s lock after every option tap read as a dead button (K8-05).
      // An unfinished brief never disables Add: pressing it takes the shopper
      // to what is missing (and the kit's commerce check needs it enabled).
      const lock = b.hasAttribute("data-wc-add") ? busy || this.adding : this.adding;
      b.disabled = lock;
      b.toggleAttribute("aria-busy", lock);
    }
  }

  /** Reflect this.options on the brief's own radios. */
  syncOptionRadios() {
    const { positions, options } = this.data;
    const set = (name, id) => {
      const r = /** @type {HTMLInputElement | null} */ (
        this.form.querySelector(`input[name="${name}"][value="${CSS.escape(String(id))}"]`)
      );
      if (r) r.checked = true;
    };
    set("wc-style", this.options[positions.style]);
    set("wc-size", this.options[positions.size]);
    const frameId = this.options[positions.frame];
    const frameValue = options[positions.frame].values.find((v) => String(v.id) === String(frameId));
    if (frameValue) {
      set("wc-finish", frameValue.finish);
      if (frameValue.finish === "framed") set("wc-frame", frameId);
    }
  }

  // ---------------------------------------------------------------- steps

  /** @param {{ initial?: boolean }} [opts] */
  route(opts = {}) {
    // `#review;1.p2…` is a coded return link (I-58): the step is before the
    // `;`. The save module applies the code and strips it; this reads only the
    // step, so a link still opens its step if that module failed to load. No
    // decodeURIComponent: a step name never needs it, and a stray % threw.
    const hash = location.hash.slice(1).replace(/%3B/gi, ";").split(";")[0];
    const wanted = this.core.STEPS.includes(hash) ? hash : null;
    if (wanted) {
      // A deep link, a progress link or Forward may land past an unfinished
      // step; the brief goes to the first step that still needs an answer, and
      // says so in the address bar too.
      const target = this.clampStep(wanted);
      if (target !== wanted) history.replaceState(history.state, "", `#${target}`);
      this.show(target, { focus: !opts.initial });
      this.toggleAttribute("data-focus-wanted", true);
    } else {
      // No step in the address. On a phone that is the brief closed, and it
      // keeps its step so it reopens where the shopper left it. On a desktop
      // there is no closed state: it is Back reaching the page's first entry,
      // and the brief goes back to step 1 with it (W-A, dev, 2026-09-25).
      this.show(this.phone.matches ? this.step : this.core.STEPS[0], { focus: false });
      this.toggleAttribute("data-focus-wanted", false);
    }
    this.applyFocusMode();
  }

  /** @param {string} wanted */
  clampStep(wanted) {
    const { STEPS } = this.core;
    const state = this.readState();
    const upload = this.uploadState();
    for (const step of STEPS.slice(0, STEPS.indexOf(wanted))) {
      if (
        this.core.validateStep(step, state, this.data, {
          uploadsDetectable: upload.detectable,
          uploadCount: upload.count,
        }).length
      )
        return step;
    }
    return wanted;
  }

  /**
   * @param {string} step
   * @param {{ focus?: boolean }} [opts]
   */
  show(step, { focus = true } = {}) {
    const { STEPS } = this.core;
    const from = STEPS.indexOf(this.step);
    const to = STEPS.indexOf(step);
    this.maxReached = Math.max(this.maxReached, to);
    this.step = step;
    this.dataset.step = step;
    this.dataset.direction = to >= from ? "forward" : "back";
    for (const section of this.querySelectorAll("[data-step]")) {
      const el = /** @type {HTMLElement} */ (section);
      const on = el.dataset.step === step;
      el.hidden = !on;
      el.toggleAttribute("data-active", on);
    }
    for (const item of this.querySelectorAll("[data-progress]")) {
      const el = /** @type {HTMLElement} */ (item);
      const i = STEPS.indexOf(el.dataset.progress || "");
      // A step reached once stays reachable (the progress link to the review
      // after an Edit); route() still refuses a step past an unfinished one.
      el.dataset.state = this.core.progressState(i, to, this.maxReached);
      const link = el.querySelector("a");
      if (i === to) link?.setAttribute("aria-current", "step");
      else link?.removeAttribute("aria-current");
      // A step not reached yet is shown, not offered: out of the tab order too,
      // not only out of reach of the pointer.
      if (link) {
        const todo = i > this.maxReached;
        link.tabIndex = todo ? -1 : 0;
        link.toggleAttribute("aria-disabled", todo);
      }
    }
    this.renderBar();
    if (step === "review") this.renderSummary();
    if (focus) {
      // `:scope` because this element carries data-step too: without it the
      // selector matched the FIRST heading, step 1's, hidden, and focus never
      // left Next (K8-02; at Review it fell to <body>).
      const heading = /** @type {HTMLElement | null} */ (
        this.querySelector(`:scope [data-step="${step}"] [data-wc-heading]`)
      );
      heading?.focus({ preventScroll: true });
      this.scrollToTop();
    }
    // A step counts as reached once the shopper has engaged with the brief:
    // counting the first step on every page view would make every product
    // view a "brief start" in the funnel.
    if (focus || this.engaged) this.track(step);
  }

  /** The shopper touched the brief: from here on, steps are counted. */
  engage() {
    if (this.engaged) return;
    this.engaged = true;
    this.track(this.step);
  }
  /** @type {boolean} */ engaged = false;
  /** @type {number} */ maxReached = 0;
  /** @type {HTMLInputElement | null} */ chipDown = null;
  /** @type {boolean} */ chipWasChecked = false;

  /**
   * Optional chips are radios, and a radio cannot be un-ticked: a second tap
   * on the chosen chip clears it, so "optional" stays true after a first tap.
   * @param {HTMLElement} target
   */
  onChipClick(target) {
    const input = /** @type {HTMLInputElement | null} */ (
      target.closest(".wc-brief-chip")?.querySelector('input[type="radio"]') || null
    );
    if (!input || !/^wc-(recipient|occasion)$/.test(input.name)) return;
    if (target !== input) return; // the label's synthetic click reaches the input next
    if (this.chipDown === input && this.chipWasChecked) {
      input.checked = false;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.chipDown = null;
  }

  scrollToTop() {
    if (this.hasAttribute("data-focus")) {
      this.scrollTo({ top: 0 });
      return;
    }
    const top = this.getBoundingClientRect().top;
    const offset =
      Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
    if (top < offset || top > innerHeight * 0.6) {
      window.scrollBy({
        top: top - offset - 16,
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    }
  }

  async next() {
    if (this.pending) await this.pending.catch(() => {});
    const errors = this.validate(this.step);
    if (errors.length) {
      this.showErrors(this.step, errors);
      return;
    }
    const { STEPS } = this.core;
    const target = this.returnToReview ? "review" : STEPS[STEPS.indexOf(this.step) + 1];
    this.returnToReview = false;
    if (target) this.go(target);
  }

  back() {
    this.returnToReview = false;
    const { STEPS } = this.core;
    const i = STEPS.indexOf(this.step);
    if (i <= 0) {
      this.close();
      return;
    }
    if (this.pushed > 0 && location.hash === `#${this.step}`) {
      // Walk the history the shopper made, so the browser's Back and ours agree.
      this.pushed--;
      history.back();
      return;
    }
    history.replaceState(history.state, "", `#${STEPS[i - 1]}`);
    this.route();
  }

  /** @param {string} step */
  go(step) {
    if (location.hash === `#${step}`) {
      this.route();
      return;
    }
    this.pushed++;
    location.hash = step; // pushes a history entry; hashchange routes
  }

  start() {
    // After a reload the phone's Start reads "Continue your portrait": keep
    // that promise. Opening the brief without the save gave an empty brief.
    if (this.pendingResume && this.saveModule) {
      this.resume(true);
      return;
    }
    this.go(this.step || "who");
  }

  close() {
    // Leave the step in place so the brief reopens where the shopper left it.
    const url = `${location.pathname}${location.search}`;
    history.replaceState(history.state, "", url);
    this.pushed = 0;
    this.route();
    /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-start]"))?.focus({ preventScroll: true });
  }

  applyFocusMode() {
    const on = this.phone.matches && this.hasAttribute("data-focus-wanted");
    const was = this.hasAttribute("data-focus");
    this.toggleAttribute("data-focus", on);
    document.documentElement.classList.toggle("wc-brief-open", on);
    if (on && !was) this.animateFocusMode();
  }

  animateFocusMode() {
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    // W1's drawer tokens (wc-motion.css), read so a retune reaches this too:
    // the plan's ceiling is 250 ms, and 280 was over it (W8's review, F6).
    const css = getComputedStyle(this);
    const t = css.getPropertyValue("--wc-duration-drawer").trim();
    const ms = t.endsWith("ms") ? parseFloat(t) : t.endsWith("s") ? parseFloat(t) * 1000 : NaN;
    this.animate(
      reduce
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [
            { opacity: 0, transform: "translateY(24px)" },
            { opacity: 1, transform: "none" },
          ],
      {
        duration: reduce ? 150 : Math.min(ms || 250, 250),
        easing: css.getPropertyValue("--wc-ease-drawer").trim() || "cubic-bezier(0.32, 0.72, 0, 1)",
      },
    );
  }

  /** @param {KeyboardEvent} event */
  trapTab(event) {
    const focusable = [
      ...this.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select,textarea,iframe,[tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => /** @type {HTMLElement} */ (el).offsetParent !== null);
    if (!focusable.length) return;
    const first = /** @type {HTMLElement} */ (focusable[0]);
    const last = /** @type {HTMLElement} */ (focusable.at(-1));
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // ---------------------------------------------------------------- validation

  /** @param {string} step */
  validate(step) {
    const upload = this.uploadState();
    return this.core.validateStep(step, this.readState(), this.data, {
      uploadsDetectable: upload.detectable,
      uploadCount: upload.count,
    });
  }

  /**
   * @param {string} step
   * @param {{ field: string, code: string }[]} errors
   */
  showErrors(step, errors) {
    const section = this.querySelector(`[data-step="${step}"]`);
    const box = /** @type {HTMLElement | null} */ (section?.querySelector("[data-wc-error]"));
    if (!box) return;
    const words = errors.map((e) => this.message(e.code)).filter(Boolean);
    box.textContent = words.join(" ");
    box.hidden = !words.length;
    for (const e of errors) {
      const wrap = section?.querySelector(`[data-wc-field="${e.field}"]`);
      // On the control itself, not on the div around it.
      const field = /** @type {HTMLElement | null} */ (
        wrap?.querySelector('input:not([type="hidden"]), textarea') || wrap || null
      );
      field?.setAttribute("aria-invalid", "true");
      field?.setAttribute("aria-describedby", box.id);
    }
    const first = /** @type {HTMLElement | null} */ (
      section?.querySelector(
        `[data-wc-field="${errors[0]?.field}"] :is(input:not([type="hidden"]), textarea), [data-wc-field="${errors[0]?.field}"]`,
      )
    );
    (first instanceof HTMLInputElement || first instanceof HTMLTextAreaElement ? first : box).focus?.({
      preventScroll: false,
    });
  }

  /** @param {HTMLElement} field */
  clearErrors(field) {
    const section = field.closest("[data-step]");
    const box = /** @type {HTMLElement | null} */ (section?.querySelector("[data-wc-error]"));
    if (box && !box.hidden) {
      box.hidden = true;
      box.textContent = "";
    }
    for (const el of section?.querySelectorAll('[aria-invalid="true"]') ?? []) el.removeAttribute("aria-invalid");
  }

  /** @param {string} code */
  message(code) {
    return this.data.messages?.[code] || "";
  }

  // ---------------------------------------------------------------- rendering

  renderAll() {
    this.renderMemorial();
    this.renderOptions();
    this.renderArtists();
    this.renderPhotos();
    this.renderText();
    this.renderBar();
    if (this.step === "review") this.renderSummary();
  }

  renderMemorial() {
    const memorial = this.readState().memorial;
    this.toggleAttribute("data-memorial", Boolean(memorial));
    for (const box of this.form.querySelectorAll("input[data-wc-extra]")) {
      const input = /** @type {HTMLInputElement} */ (box);
      const allowed = !memorial || input.dataset.memorialOk === "true";
      const row = /** @type {HTMLElement | null} */ (input.closest("[data-wc-extra-row]"));
      if (row) row.hidden = !allowed;
      if (!allowed) input.checked = false;
    }
  }

  /** @param {boolean} on */
  setMemorial(on) {
    // The memorial path asks for the name and the dates separately; words
    // already typed move across instead of vanishing behind the switch.
    const f = this.form;
    const line = /** @type {HTMLInputElement | null} */ (f.elements.namedItem("wc-text-line"));
    const name = /** @type {HTMLInputElement | null} */ (f.elements.namedItem("wc-text-name"));
    if (on && line?.value.trim() && name && !name.value.trim()) name.value = line.value;
    if (!on && name?.value.trim() && line && !line.value.trim()) line.value = name.value;
    this.toggleAttribute("data-memorial", on);
    // The memorial path asks for no occasion: the occasion is the memory.
    if (on)
      for (const r of this.form.querySelectorAll('input[name="wc-occasion"]'))
        /** @type {HTMLInputElement} */ (r).checked = false;
    this.announce(this.message(on ? "memorial_on" : "memorial_off"));
  }

  /** Hide impossible combinations and label each choice with its difference. */
  renderOptions() {
    const { positions, options } = this.data;
    const core = this.core;
    const current = core.variantFor(this.data, this.options);
    const base = current?.price ?? 0;

    const label = (el, price, isCurrent) => {
      const out = /** @type {HTMLElement | null} */ (el.querySelector("[data-wc-delta]"));
      if (out) out.textContent = core.priceLabel(price, base, isCurrent, (c) => this.money(c));
    };

    // Sizes: every size the current style offers, priced at the finish kept if
    // possible (resolveCombination is what a click would actually do).
    for (const card of this.form.querySelectorAll('input[name="wc-size"]')) {
      const input = /** @type {HTMLInputElement} */ (card);
      const combo = core.resolveCombination(this.data, this.options, positions.size, input.value);
      const variant = combo && core.variantFor(this.data, combo);
      const wrap = /** @type {HTMLElement} */ (input.closest("[data-wc-choice]"));
      wrap.hidden = !variant;
      label(wrap, variant?.price, input.value === this.options[positions.size]);
      const finishes = core.availableValues(
        this.data,
        positions.frame,
        this.options.map((id, i) => (i === positions.size ? input.value : i === positions.frame ? null : id)),
      );
      const only = [...finishes].map((id) => options[positions.frame].values.find((v) => String(v.id) === id)?.finish);
      const sizeNote = /** @type {HTMLElement | null} */ (wrap.querySelector("[data-wc-size-note]"));
      if (sizeNote) sizeNote.hidden = !(only.length > 0 && only.every((f) => f === "canvas"));
    }

    // Finishes and frame colours: only those this size and style come in.
    const fixed = this.options.map((id, i) => (i === positions.frame ? null : id));
    const available = core.availableValues(this.data, positions.frame, fixed);
    const frameValues = options[positions.frame].values;
    for (const card of this.form.querySelectorAll('input[name="wc-finish"]')) {
      const input = /** @type {HTMLInputElement} */ (card);
      const values = frameValues.filter((v) => v.finish === input.value && available.has(String(v.id)));
      const wrap = /** @type {HTMLElement} */ (input.closest("[data-wc-choice]"));
      wrap.hidden = values.length === 0;
      const currentFrame = frameValues.find((v) => String(v.id) === String(this.options[positions.frame]));
      const pick = values.find((v) => String(v.id) === String(currentFrame?.id)) || values[0];
      const variant =
        pick &&
        core.variantFor(
          this.data,
          this.options.map((id, i) => (i === positions.frame ? String(pick.id) : id)),
        );
      label(wrap, variant?.price, currentFrame?.finish === input.value);
    }
    for (const card of this.form.querySelectorAll('input[name="wc-frame"]')) {
      const input = /** @type {HTMLInputElement} */ (card);
      const wrap = /** @type {HTMLElement} */ (input.closest("[data-wc-choice]"));
      const value = frameValues.find((v) => String(v.id) === input.value);
      wrap.hidden = value?.finish !== "framed" || !available.has(String(input.value));
    }
    const framesRow = /** @type {HTMLElement | null} */ (this.form.querySelector("[data-wc-frames]"));
    if (framesRow) {
      const finish = frameValues.find((v) => String(v.id) === String(this.options[positions.frame]))?.finish;
      framesRow.hidden = finish !== "framed";
    }
  }

  /**
   * Artist cards against the chosen style. An artist who does not paint it
   * stays on screen, greyed and disabled, with the styles they do paint (Luke,
   * 1 Oct: "grey out", not hide). A chosen artist the new style excludes goes
   * back to "Next available", and says so on screen and aloud, rather than
   * silently carrying a request the artist cannot take (K12).
   */
  renderArtists() {
    const style = /** @type {HTMLElement | null} */ (
      this.form.querySelector('input[name="wc-style"]:checked')?.closest("[data-style-key]")
    );
    const key = style?.dataset.styleKey || "";
    const styleChanged = key !== this.artistStyleKey;
    this.artistStyleKey = key;
    let enabled = 0;
    let reset = false;
    for (const card of this.querySelectorAll("[data-wc-artist]")) {
      const el = /** @type {HTMLElement} */ (card);
      const on = this.core.paintsStyle(el.dataset.styles, key);
      el.toggleAttribute("data-unavailable", !on);
      const input = /** @type {HTMLInputElement | null} */ (el.querySelector('input[name="wc-artist"]'));
      if (input) {
        if (!on && input.checked) {
          input.checked = false;
          reset = true;
        }
        input.disabled = !on;
        if (on) input.removeAttribute("aria-disabled");
        else input.setAttribute("aria-disabled", "true");
      }
      const why = /** @type {HTMLElement | null} */ (el.querySelector("[data-wc-artist-why]"));
      if (why) why.hidden = on;
      if (on) enabled++;
    }
    if (reset) {
      const next = /** @type {HTMLInputElement | null} */ (
        this.form.querySelector(`input[name="wc-artist"][value="${this.core.NEXT_AVAILABLE}"]`)
      );
      if (next) next.checked = true;
      this.announce(this.message("artist_reset"));
    }
    // The on-screen line follows the latest style change only: a later style
    // that takes nobody away clears it, as does the shopper choosing again.
    const note = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-artist-reset]"));
    if (note && (reset || styleChanged)) {
      note.textContent = reset ? this.message("artist_reset") : "";
      note.hidden = !reset;
    }
    const none = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-no-artists]"));
    if (none) none.hidden = enabled > 0;
  }
  /** @type {string | undefined} */ artistStyleKey;

  /** The shopper chose again: the "we'll match you instead" line has done its job. */
  clearArtistReset() {
    const note = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-artist-reset]"));
    if (note && !note.hidden) {
      note.hidden = true;
      note.textContent = "";
    }
  }

  /** Photos now or later: the upload slot shows only for "now". */
  renderPhotos() {
    const choice =
      /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[name="wc-photos"]:checked'))?.value || "";
    this.dataset.photos = choice;
    // No upload app in the slot (not installed, or it failed to load): "now"
    // cannot be honoured, so it is not offered, and "later" is the answer.
    const slot = this.querySelector("[data-wc-uploads]");
    const hasApp = Boolean(slot && slot.children.length);
    const now = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-photos-now]"));
    if (now) now.hidden = !hasApp;
    const missing = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-no-uploader]"));
    if (missing) missing.hidden = hasApp;
    if (!hasApp && choice !== "later") {
      const later = /** @type {HTMLInputElement | null} */ (
        this.form.querySelector('input[name="wc-photos"][value="later"]')
      );
      if (later) later.checked = true;
      this.dataset.photos = "later";
      // The brief chose "later", not the shopper.
      this.photosAutoLater = true;
    } else if (hasApp && this.photosAutoLater && choice === "later") {
      // The upload app mounted after the brief started (it loads on its own
      // clock): give back the preselected "now" (Luke's audit A34, 14 Oct). A
      // shopper's own choice, or a restored brief, has cleared the flag.
      const now = /** @type {HTMLInputElement | null} */ (
        this.form.querySelector('input[name="wc-photos"][value="now"]')
      );
      if (now) now.checked = true;
      this.dataset.photos = "now";
      this.photosAutoLater = false;
    }
    this.photos?.render(this);
  }
  /** @type {boolean} */ photosAutoLater = false;

  /** One row per subject: a name, and (after the spike) its own upload field. */
  renderSubjects() {
    const list = this.querySelector("[data-wc-subjects]");
    if (!list) return;
    const { clampInt, LIMITS } = this.core;
    const f = this.form;
    const people = clampInt(
      /** @type {HTMLInputElement} */ (f.elements.namedItem("wc-people"))?.value,
      0,
      LIMITS.people,
    );
    const pets = clampInt(/** @type {HTMLInputElement} */ (f.elements.namedItem("wc-pets"))?.value, 0, LIMITS.pets);
    const existing = [...list.querySelectorAll('input[name="wc-subject"]')].map(
      (i) => /** @type {HTMLInputElement} */ (i).value,
    );
    const template = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector("template[data-wc-subject-template]")
    );
    if (!template) return;
    const rows = [];
    for (let i = 0; i < people + pets; i++) {
      const isPet = i >= people;
      const n = isPet ? i - people + 1 : i + 1;
      const row = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const input = /** @type {HTMLInputElement} */ (row.querySelector("input"));
      const labelEl = /** @type {HTMLElement} */ (row.querySelector("label"));
      const id = `${this.id}-subject-${i}`;
      input.id = id;
      labelEl.htmlFor = id;
      labelEl.textContent = (isPet ? this.data.labels.petN : this.data.labels.personN).replace("%n%", String(n));
      input.placeholder = isPet ? this.data.labels.petPlaceholder : this.data.labels.personPlaceholder;
      input.value = existing[i] ?? "";
      rows.push(row);
    }
    list.replaceChildren(...rows);
    list.toggleAttribute("hidden", rows.length === 0);
  }

  renderText() {
    const state = this.readState();
    const fields = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-text-fields]"));
    if (fields) fields.hidden = !state.addText;
    const counter = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-text-count]"));
    if (counter) counter.textContent = this.data.labels.textCount.replace("%n%", String(state.textLine.length));
  }

  renderBar() {
    if (!this.core) return;
    const state = this.readState();
    const lines = this.core.buildLines(state, this.data);
    const t = this.core.totals(lines, state.payment, this.data);
    const set = (sel, text) => {
      for (const el of this.querySelectorAll(sel)) el.textContent = text;
    };
    set("[data-wc-due]", this.money(t.due));
    set("[data-wc-total]", this.money(t.total));
    set("[data-wc-later]", this.money(t.later));
    this.toggleAttribute("data-plan", Boolean(state.payment && state.payment !== this.core.PAY_NOW));

    const { STEPS } = this.core;
    const i = STEPS.indexOf(this.step);
    const nextStep = this.returnToReview ? "review" : STEPS[i + 1];
    const nextLabel = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-next-label]"));
    if (nextLabel && nextStep) {
      const name = this.data.stepNames?.[nextStep] || nextStep;
      nextLabel.textContent = (this.returnToReview ? this.data.labels.backToReview : this.data.labels.nextTo).replace(
        "%step%",
        name,
      );
    }
    this.toggleAttribute("data-last", this.step === "review");
    this.scheduleAnnounce(t);
  }

  /** The review's summary and itemised order: assets/wc-brief-review.js. */
  renderSummary() {
    this.review.renderSummary(this);
  }

  /** @param {{ due: number, total: number }} t */
  scheduleAnnounce(t) {
    // Prices change as the shopper taps; say the result once they pause, not
    // at every step of the counter.
    clearTimeout(this.announceTimer);
    this.announceTimer = window.setTimeout(() => {
      const text = this.data.labels.priceAnnounce
        .replace("%total%", this.money(t.total))
        .replace("%due%", this.money(t.due));
      if (text !== this.lastPriceAnnounce) {
        this.lastPriceAnnounce = text;
        this.announce(text);
      }
    }, 700);
  }
  /** @type {string} */ lastPriceAnnounce = "";

  /** @param {string} text */
  announce(text) {
    const live = /** @type {HTMLElement | null} */ (this.querySelector("[data-wc-live]"));
    if (!live || !text) return;
    live.textContent = "";
    requestAnimationFrame(() => (live.textContent = text));
  }

  /** @param {number} cents */
  money(cents) {
    return this.formatMoney(cents, this.data.moneyFormat, this.data.currency);
  }

  /** @param {HTMLElement} button */
  bump(button) {
    const input = /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem(button.dataset.wcCount || ""));
    if (!input) return;
    const delta = Number(button.dataset.delta || 0);
    const min = Number(input.min || 0);
    const max = Number(input.max || 99);
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + delta));
    if (String(next) === input.value) return;
    input.value = String(next);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // ---------------------------------------------------------------- the add

  /** One items[] POST, the drawer's event and what follows: assets/wc-brief-cart.js. */
  add() {
    return this.cart.add(this);
  }

  // ---------------------------------------------------------------- autosave

  // The saving and the resume prompt live in assets/wc-brief-save.js, loaded
  // with the core at start (theme check refuses a first-load script over
  // 60 KB). These are the element's handles on it.

  get saveKey() {
    return this.core.autosaveKey(this.dataset.market || "", this.dataset.productHandle || "");
  }

  scheduleSave() {
    if (this.restoring) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.save(), 400);
  }

  save() {
    this.saveModule?.save(this);
  }

  clearSave() {
    clearTimeout(this.saveTimer);
    this.saveModule?.clearSave(this);
  }

  offerResume() {
    this.saveModule?.offerResume(this);
  }

  /** @param {boolean} yes */
  resume(yes) {
    this.saveModule?.resume(this, yes);
  }

  /** @type {any} */ pendingResume = null;
  /** @type {any} */ saveModule = null;
  /** @type {any} */ review = null;
  /** @type {any} */ cart = null;

  // ---------------------------------------------------------------- email and tracking

  /**
   * "Email me my brief" lives in its own module (assets/wc-brief-email.js),
   * loaded on the tap: it is off until Camille's flow is live, and theme
   * check refuses a first-load script over 60 KB.
   */
  async emailBrief() {
    const module = await import(this.dataset.email);
    return module.emailBrief(this);
  }

  /**
   * GA4 `brief_step` with `theme_arm`, and a Clarity tag, once per step per
   * brief (plan §3.10). Automated browsers have these answered locally by the
   * kit's analytics block, so the checks never count as sessions.
   * @param {string} step
   */
  track(step) {
    if (this.tracked.has(step)) return;
    this.tracked.add(step);
    const w = /** @type {any} */ (window);
    const arm = w.wcThemeArm || "variant";
    const index = this.core.STEPS.indexOf(step);
    try {
      if (typeof w.gtag === "function") {
        w.gtag("event", "brief_step", {
          brief_step: step,
          step_index: index >= 0 ? index + 1 : 6,
          theme_arm: arm,
          product_handle: this.data.productHandle,
          memorial: this.hasAttribute("data-memorial"),
        });
      }
      if (typeof w.clarity === "function") {
        w.clarity("set", "brief_step", step);
        w.clarity("event", `brief_${step}`);
      }
    } catch {
      /* tracking never breaks the brief */
    }
  }
}

if (!customElements.get("wc-brief")) customElements.define("wc-brief", WcBrief);
